import { useState } from "react";
import { toast } from "sonner";
import { Upload, Download, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const TEMPLATE = "nome;cognome;codice_fiscale;data_nascita;luogo_nascita;sesso;email;telefono;numero_tessera;scadenza_tessera;stato_tesseramento;asd_ssd;settore\n" +
  "Mario;Esempio;XMPMRA90A01H501A;1990-01-01;Roma;M;mario@esempio.it;3330000000;MSP-X-000001;2026-12-31;Valido;ASD Esempio;SMN\n";

export function ImportDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const downloadTemplate = () => {
    const blob = new Blob(["\ufeff" + TEMPLATE], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "modello-importazione-atleti.csv";
    a.click();
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/athletes/import", fd, { timeout: 120000 });
      setReport(data);
      toast.success(`Importazione completata: ${data.created} atleti inseriti`);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setReport(null); setFile(null); } }}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="import-dialog">
        <DialogHeader><DialogTitle>Importazione massiva CSV / Excel</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">
          Il sistema controlla i duplicati tramite codice fiscale e numero di tessera: le anagrafiche già presenti non vengono reinserite.
          Colonna <span className="font-mono">settore</span>: codice del settore (es. SMN, SMCS).
        </p>
        <Button variant="outline" onClick={downloadTemplate} className="w-fit" data-testid="import-template-btn"><Download className="mr-2 h-4 w-4" />Scarica modello CSV</Button>
        <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-6 text-sm text-slate-600 transition-colors hover:border-blue-500">
          <Upload className="h-6 w-6 text-slate-400" />
          {file ? <span className="font-medium text-slate-900">{file.name}</span> : "Seleziona un file .csv o .xlsx"}
          <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="import-file-input" />
        </label>
        <Button onClick={submit} disabled={!file || busy} className="bg-[#0F2042] hover:bg-[#1E3A8A]" data-testid="import-submit-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Avvia importazione"}
        </Button>
        {report && (
          <div className="space-y-3 text-sm" data-testid="import-report">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-emerald-50 p-3"><div className="text-2xl font-bold text-emerald-700">{report.created}</div>Inseriti</div>
              <div className="rounded-lg bg-amber-50 p-3"><div className="text-2xl font-bold text-amber-700">{report.duplicates.length}</div>Duplicati</div>
              <div className="rounded-lg bg-red-50 p-3"><div className="text-2xl font-bold text-red-700">{report.errors.length}</div>Errori</div>
            </div>
            {report.duplicates.map((d) => <div key={`d${d.riga}`} className="text-amber-800">Riga {d.riga}: {d.codice_fiscale} / {d.numero_tessera} già presente ({d.esistente})</div>)}
            {report.errors.map((d) => <div key={`e${d.riga}`} className="text-red-700">Riga {d.riga}: {d.errore}</div>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
